import { left, right } from '@sweet-monads/either';
import type { Either } from '@sweet-monads/either';

import { AbstractError } from '@/domain/errors/abstract-error.js';
import { ServerError } from '@/domain/errors/server-error.js';
import type { ClientOptionModel, CreateUserParams, ProjectOptionModel, SaveAssignmentParams, UpdateUserParams, UserAssignmentModel, UserModel } from '@/domain/models/user.js';
import type { UserRepository } from '@/domain/repositories/user-repository.js';
import type {
    BlockUser,
    CreateUser,
    LoadClientOptions,
    LoadProjectOptions,
    LoadUserAssignments,
    LoadUserById,
    LoadUsers,
    ResetUserPassword,
    SaveUserAssignments,
    UnblockUser,
    UpdateUser
} from '@/domain/use-cases/users.js';

export class LoadUsersUseCase implements LoadUsers {
    constructor(private readonly repository: UserRepository) {}

    public async execute(params?: {
        search?: string;
        profile?: string;
        status?: string;
        page?: number;
        limit?: number;
    }): Promise<Either<AbstractError, { data: UserModel[]; total: number; page: number; totalPages: number }>> {
        try {
            return right(await this.repository.findAll(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadUserByIdUseCase implements LoadUserById {
    constructor(private readonly repository: UserRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, UserModel>> {
        try {
            return right(await this.repository.findById(id));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateUserUseCase implements CreateUser {
    constructor(private readonly repository: UserRepository) {}

    public async execute(params: CreateUserParams): Promise<Either<AbstractError, UserModel>> {
        try {
            return right(await this.repository.create(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateUserUseCase implements UpdateUser {
    constructor(private readonly repository: UserRepository) {}

    public async execute(id: string, params: UpdateUserParams): Promise<Either<AbstractError, UserModel>> {
        try {
            return right(await this.repository.update(id, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class BlockUserUseCase implements BlockUser {
    constructor(private readonly repository: UserRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.block(id);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UnblockUserUseCase implements UnblockUser {
    constructor(private readonly repository: UserRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.unblock(id);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ResetUserPasswordUseCase implements ResetUserPassword {
    constructor(private readonly repository: UserRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.resetPassword(id);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadUserAssignmentsUseCase implements LoadUserAssignments {
    constructor(private readonly repository: UserRepository) {}

    public async execute(userId: string): Promise<Either<AbstractError, UserAssignmentModel[]>> {
        try {
            return right(await this.repository.findAssignments(userId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class SaveUserAssignmentsUseCase implements SaveUserAssignments {
    constructor(private readonly repository: UserRepository) {}

    public async execute(userId: string, assignments: SaveAssignmentParams[]): Promise<Either<AbstractError, UserAssignmentModel[]>> {
        try {
            return right(await this.repository.saveAssignments(userId, assignments));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadClientOptionsUseCase implements LoadClientOptions {
    constructor(private readonly repository: UserRepository) {}

    public async execute(): Promise<Either<AbstractError, ClientOptionModel[]>> {
        try {
            return right(await this.repository.findClientOptions());
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadProjectOptionsUseCase implements LoadProjectOptions {
    constructor(private readonly repository: UserRepository) {}

    public async execute(clientId: string): Promise<Either<AbstractError, ProjectOptionModel[]>> {
        try {
            return right(await this.repository.findProjectOptions(clientId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}
